<template>
  <div :key="`index-menu-${uid}`" class="index-menu">
    <item-header type="index" :choosable="true" :selected-id="ctx.index.id" @select="selectIndex">
      <template #content>
        <editable-item-title :item="ctx.index" />
      </template>
    </item-header>

    <drawer-block
      :title="$t('labels.general')"
      :open="openDrawers.indexGeneral"
      @update:open="openDrawers.indexGeneral = $event"
    >
      <div class="column gap-4">
        <input-text
          ref="titleField"
          :placeholder="$t('labels.title')"
          :prevent-submit="true"
          :label="$t('labels.title')"
          :value="ctx.index.title"
          @enter="descriptionField?.focus()"
          @update:value="ctx.updateKey('title', $event)"
        />
        <input-texteditor
          ref="descriptionField"
          :label="$t('labels.description')"
          :value="ctx.index.description"
          @update:value="ctx.updateKey('description', $event)"
        />
      </div>
    </drawer-block>

    <drawer-block
      v-model:open="openDrawers.indexValue"
      :title="$t('labels.value')"
    >
      <div class="column">
        <expression-input-with-template
          :expression="ctx.index.valueExpression"
          :parse-context="{
            ...indexStore.indexParseContext,
            affectedType: 'index',
            affectedId: ctx.index.id,
            randomKey: ctx.index.uid,
            randomValues: randomValuesStore.randomValues,
            rounds: [{ elementValues }]
          }"
          :input-mode="ctx.index.valueInputMode"
          @update:input-mode="ctx.updateKey('valueInputMode', $event)"
          @update:expression="ctx.updateKey('valueExpression', $event)"
        />

        <div>
          <effect-expression
            v-slot="{ value }"
            :expression="ctx.index.valueExpression"
            :parse-context="{
              ...indexStore.indexParseContext,
              affectedType: 'index',
              affectedId: ctx.index.id,
              randomKey: ctx.index.uid,
              randomValues: randomValuesStore.randomValues,
            }"
            number-format="standard"
          >
            = <strong>{{ value }}</strong
            >&thinsp;{{ evaluatedExpression?.unit }}
          </effect-expression>
        </div>

        <div v-if="evaluatedExpression">
          (Min: {{ formatNumber(evaluatedExpression.minValue) }}, Max:
          {{ formatNumber(evaluatedExpression.maxValue) }})
        </div>

        <div class="line">
          <input-text
            :label="$t('labels.unit')"
            :placeholder="
              evaluatedExpression.unit +
              (evaluatedExpression.unitValid ? '' : '??')
            "
            :value="ctx.index.unit || ''"
            @update:value="ctx.updateKey('unit', $event)"
          />
        </div>
      </div>
    </drawer-block>

    <!-- IMPACTS -->
    <drawer-block
      v-if="features.impact"
      v-model:open="openDrawers.indexRelations"
      :title="$t('objectNames.impact', 2)"
    >
      <div class="column gap-6 mt-1">
        <relation-list
          type="outgoing"
          :element-uid="ctx.index.uid"
          :selected-relation-id="(subSelection as RelationItem)?.id"
          @select="emit('select', $event)"
        />
      </div>
    </drawer-block>

    <drawer-block
      v-if="features.stakeholder"
      v-model:open="openDrawers.indexStakeholders"
      :title="$t('objectNames.stakeholder', 2)"
    >
      <stakeholder-list
        item-type="index"
        :item-id="ctx.index.id"
        @select="emit('select', $event)"
        @deselect="emit('deselect', $event)"
      />
    </drawer-block>

    <drawer-block
      v-if="features.design"
      v-model:open="openDrawers.indexDesign"
      :title="$t('labels.design')"
    >
      <element-design-menu :element-uid="props.uid" />
    </drawer-block>

    <menu-group class="mt-auto">
      <button
        class="button button_secondary mv-2"
        type="button"
        @click="deleteIndex"
      >
        {{ $t("actions.deleteIndex", 1) }}
      </button>
    </menu-group>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";

import { formatNumber } from "@/helpers/utility/Formatting";

import MenuGroup from "@core/ui/MenuGroup.vue";
import InputText from "@core/input/InputText.vue";
import DrawerBlock from "@core/ui/DrawerBlock.vue";
import InputTexteditor from "@core/input/TextEditor.vue";

import ElementDesignMenu from "@editor/items/element/ElementDesignMenu.vue";
import ItemHeader from "@editor/components/ItemHeader.vue";
import RelationList from "@editor/items/relation/RelationList.vue";
import EffectExpression from "@editor/items/effect/EffectExpression.vue";
import StakeholderList from "@editor/items/stakeholder/StakeholderList.vue";
import EditableItemTitle from "@editor/components/EditableItemTitle.vue";
import ExpressionInputWithTemplate from "@editor/items/expression/ExpressionInputWithTemplate.vue";

import { useIndex } from "./useIndex";
import { useElements } from "../element/elements";
import { useDrawerStates } from "@editor/store/editor/drawerStates.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useIndexStore } from "./indices.store";
import { useFeatures } from "@editor/store/features.store";
import { useRandomValuesStore } from "../expression/expressionRandomValues";

import type { PartialIdentity, Uid } from "@/helpers/utility/Id";
import type { RelationItem } from "../relation/RelationItem";

interface Props {
  uid: Uid<"index">;
  nodeUid?: Uid<"sceneNode">;
  subSelection?: { type: string; id: number } | null;
}
const props = withDefaults(defineProps<Props>(), {
  subSelection: null
});

const emit = defineEmits<{
  (
    e: "select",
    item: PartialIdentity<"stakeholder" | "relation"> | null
  ): void;
  (
    e: "deselect",
    item: PartialIdentity<"stakeholder" | "relation"> | null
  ): void;
}>();

const openDrawers = useDrawerStates();
const ctx = useIndex(() => props.uid);
const features = useFeatures();
const randomValuesStore = useRandomValuesStore();

const descriptionField = ref<InstanceType<typeof InputTexteditor>>();

const selectionStore = useSelectionStore();
const indexStore = useIndexStore();

const indices = computed(() => indexStore.items);

// ======================
// ACTIONS

function selectIndex(indexId: number | null) {
  if (indexId === ctx.index.id) return;
  if (indexId == null) {
    selectionStore.clearAll();
    return;
  }

  const index = indices.value[indexId];
  if (index == null) {
    selectionStore.clearAll();
  } else {
    selectionStore.select(index);
  }
}

function deleteIndex() {
  ctx.delete();
}

const elementValues = computed(() => useElements().elementValues);

const evaluatedExpression = computed(() => {
  return indexStore.expressionValues[ctx.index.uid];
});
</script>

<style lang="scss">
.index-menu {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}
</style>
